import { useEffect, useRef, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Upload, Trash2, ImageIcon, Video } from 'lucide-react';

const MEDIA_URL = (v) => `${process.env.REACT_APP_BACKEND_URL}/api/promo/media?v=${v}`;

export default function MediaBannerTab() {
  const [banner, setBanner] = useState(undefined); // undefined = loading, null = none
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    staffApi
      .get('/promo')
      .then(({ data }) => setBanner(data.banner))
      .catch(() => setBanner(null));

  useEffect(() => {
    load();
  }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Please choose an image or a video file');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await staffApi.post('/promo', fd);
      toast.success('Promo media saved');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await staffApi.delete('/promo');
      toast.success('Promo media deleted');
      setBanner(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md space-y-4" data-testid="media-banner-tab">
      <div>
        <h2 className="font-heading text-lg font-700 text-charcoal">Customer QR banner</h2>
        <p className="text-sm text-muted-ink">
          A single square image or looping video shown to every customer, just above their QR code.
          Delete it to show nothing (the QR moves up).
        </p>
      </div>

      <div className="rounded-2xl bg-white border border-border/40 shadow-sm overflow-hidden">
        {banner === undefined ? (
          <div className="aspect-square bg-bone animate-pulse" />
        ) : banner ? (
          banner.mediaType === 'VIDEO' ? (
            <video
              data-testid="media-banner-preview-video"
              src={MEDIA_URL(banner.version)}
              className="w-full aspect-square object-cover block"
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img
              data-testid="media-banner-preview-image"
              src={MEDIA_URL(banner.version)}
              alt="Current promo banner"
              className="w-full aspect-square object-cover block"
            />
          )
        ) : (
          <div className="aspect-square bg-bone flex flex-col items-center justify-center gap-2 text-muted-ink">
            <ImageIcon size={28} />
            <p className="text-sm" data-testid="media-banner-empty">No banner set</p>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          data-testid="media-banner-file-input"
          onChange={onPick}
        />
        <Button
          data-testid="media-banner-upload-btn"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="flex-1 bg-saffron hover:bg-saffron-hover"
        >
          <Upload size={16} className="mr-2" /> {banner ? 'Replace media' : 'Upload media'}
        </Button>
        {banner && (
          <Button
            data-testid="media-banner-delete-btn"
            variant="outline"
            disabled={busy}
            onClick={remove}
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 size={16} className="mr-2" /> Delete
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-ink flex items-center gap-1.5">
        <Video size={12} /> Images (JPG/PNG/WebP) or MP4 video up to 50MB. Videos autoplay muted and loop.
      </p>
    </div>
  );
}
